import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Page } from "@/components/Layout";
import IntroAudioSummary from "@/components/IntroAudioSummary";
import DirectoryHelpBand from "@/components/DirectoryHelpBand";
import { api } from "@/lib/api";
import { useProSignup } from "@/components/ProfessionalSignupModal";
import { useCurrency } from "@/lib/currency";
import Seo from "@/components/Seo";
import { MagnifyingGlass, ArrowRight, X, Sparkle, Brain, Heart, CheckCircle, ChatCircle, Clock } from "@phosphor-icons/react";
import IntroAudioPlayer from "@/components/IntroAudioPlayer";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const audioSrc = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  return `${BACKEND}${u}`;
};

const SMALL_WORDS = new Set(["a", "an", "the", "to", "of", "and", "or", "in", "with", "for"]);
const tagLabel = (t) =>
  t.split("-").map((w, i) => {
    if (i > 0 && SMALL_WORDS.has(w.toLowerCase())) return w.toLowerCase();
    return w.charAt(0).toUpperCase() + w.slice(1);
  }).join(" ");

const tagGroups = [
  {
    label: "Mental & Emotional",
    color: "var(--md-primary)",
    icon: Heart,
    tags: ["anxiety", "depression", "burnout", "trauma", "self-esteem", "grief", "relationships", "stress"],
  },
  {
    label: "Cognitive",
    color: "var(--md-secondary-dark)",
    icon: Brain,
    tags: ["focus", "memory", "brain-fog", "ADHD", "overthinking", "study-difficulties", "decision-making", "concentration"],
  },
  {
    label: "Life Transitions",
    color: "var(--md-accent-hover, #C29A1F)",
    icon: Sparkle,
    tags: ["moving-countries", "LGBTQIA+", "career", "marriage-divorce", "becoming-a-parent", "retirement", "adapting-to-a-diagnosis"],
  },
];

export default function Therapists() {
  const [items, setItems] = useState([]);
  const [tag, setTag] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const { open: openProSignup } = useProSignup();
  const { format } = useCurrency();

  // `fetchData` is defined inside the effect and `api` is module-scope, so
  // [tag, q] is the complete dependency set (re-fetch on filter changes only).
  useEffect(() => {
    let alive = true;
    const fetchData = async () => {
      setLoading(true);
      const params = {};
      if (tag) params.tag = tag;
      if (q) params.q = q;
      try {
        const r = await api.get("/therapists", { params });
        if (alive) setItems(r.data);
      } finally {
        if (alive) setLoading(false);
      }
    };
    fetchData();
    return () => { alive = false; };
  }, [tag, q]);

  return (
    <Page>
      <Seo title="Find a Therapist" description="Browse and book directly with globally trained, culturally competent therapists at MindDhara." />

      {/* HERO */}
      <section className="therapist-hero" data-testid="therapists-hero">
        <div className="md-container">
          <div className="therapist-hero-grid">
            <div className="therapist-hero-copy">
              <div className="md-label-eyebrow mb-3">Couch · Therapist Directory</div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif mb-5" style={{ lineHeight: 1.05 }}>
                Browse therapists<br /><span style={{ color: "var(--md-primary)" }}>Book directly</span>
              </h1>
              <p className="text-base md:text-lg max-w-xl" style={{ color: "var(--md-text-soft)" }}>
                Globally trained, culturally competent.<br />Available in your language, on your time.
              </p>

              <div className="therapist-search-wrap mt-7" data-testid="therapist-search-wrap">
                <MagnifyingGlass size={18} className="therapist-search-ico" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by name or concern…"
                  className="therapist-search-input"
                  data-testid="therapist-search-input"
                />
                {q && (
                  <button onClick={() => setQ("")} className="therapist-search-clear" aria-label="Clear search" data-testid="therapist-search-clear">
                    <X size={14} weight="bold" />
                  </button>
                )}
              </div>

              <div className="therapist-hero-stats">
                <span><strong>{items.length}</strong> Therapists</span>
                <span aria-hidden>·</span>
                <span>12+ Languages</span>
              </div>
            </div>

            <aside className="therapist-hero-card" data-testid="therapist-pro-cta">
              <div className="md-label-eyebrow mb-2" style={{ color: "var(--md-secondary)" }}>For Professionals</div>
              <h3 className="text-2xl font-serif" style={{ color: "var(--md-bg)" }}>Join the MindDhara directory</h3>
              <p className="text-sm mt-2.5" style={{ color: "rgba(251,250,247,0.78)" }}>
                Practitioner-friendly platform with vetted clients, transparent payouts and full admin support.
              </p>
              <button type="button" onClick={openProSignup} className="btn-accent text-sm mt-5" data-testid="therapist-hero-apply-cta">
                Apply to join <ArrowRight size={14} weight="bold" />
              </button>
              <ul className="therapist-hero-card-list">
                <li><CheckCircle size={14} weight="fill" /> Vetted, intent-rich clients</li>
                <li><CheckCircle size={14} weight="fill" /> Transparent monthly payouts</li>
                <li><CheckCircle size={14} weight="fill" /> Calendar & reminders included</li>
              </ul>
            </aside>
          </div>
        </div>
      </section>

      {/* FILTERS */}
      <section className="md-container therapist-filters" data-testid="therapist-filters">
        <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
          <div>
            <div className="md-label-eyebrow mb-1">Filter by concern</div>
            <h2 className="text-lg font-serif">What would you like support with?</h2>
          </div>
          {tag && (
            <button onClick={() => setTag("")} className="btn-outline text-xs" data-testid="therapist-clear-tag">
              Clear filter <X size={12} weight="bold" />
            </button>
          )}
        </div>

        <div className="therapist-tag-groups">
          {tagGroups.map((g) => (
            <div key={g.label} className="therapist-tag-group" data-testid={`tag-group-${g.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="therapist-tag-group-head" style={{ color: g.color }}>
                <g.icon size={18} weight="duotone" />
                <span>{g.label}</span>
              </div>
              <div className="therapist-tag-chips">
                {g.tags.map((t) => {
                  const active = tag === t;
                  return (
                    <button
                      key={t}
                      onClick={() => setTag(active ? "" : t)}
                      className={`therapist-chip ${active ? "therapist-chip-active" : ""}`}
                      data-testid={`therapist-tag-${t}`}
                    >
                      {tagLabel(t)}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* LIST */}
      <section className="md-container" data-testid="therapist-list-section">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
          <h2 className="text-2xl font-serif">
            {tag ? <>Therapists for <span style={{ color: "var(--md-primary)" }}>{tagLabel(tag)}</span></> : "Wellbeing Professionals"}
          </h2>
          {!loading && <span className="text-sm" style={{ color: "var(--md-text-muted)" }}>{items.length} results</span>}
        </div>

        {(() => {
          if (loading) {
            return (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => <div key={i} className="md-card therapist-skel" />)}
              </div>
            );
          }
          if (items.length === 0) {
            return (
              <div className="md-card text-center py-12" data-testid="therapist-empty">
                <h3 className="text-xl font-serif mb-2">No matches for this filter</h3>
                <p className="text-sm" style={{ color: "var(--md-text-soft)" }}>Try a different concern or clear the filter to see everyone.</p>
                {tag && <button className="btn-outline text-sm mt-5" onClick={() => setTag("")}>Show all therapists</button>}
              </div>
            );
          }
          return (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="therapist-grid">
            {items.map((t) => (
              <Link key={t.id} to={`/therapists/${t.id}?trail=therapists`} className="md-card md-card-hover therapist-card" data-testid={`therapist-card-${t.id}`}>
                <div className="flex gap-4">
                  {t.photo_url ? (
                    <img src={t.photo_url} alt={t.name} className="w-16 h-16 rounded-full object-cover therapist-card-photo" />
                  ) : (
                    <div className="w-16 h-16 rounded-full flex items-center justify-center" style={{ background: "var(--md-bg-alt)" }}>
                      <Heart size={22} weight="duotone" style={{ color: "var(--md-primary)" }} />
                    </div>
                  )}
                  <div className="min-w-0">
                    <h3 className="font-serif text-xl truncate">{t.name}</h3>
                    <p className="text-sm truncate" style={{ color: "var(--md-text-soft)" }}>{t.title}</p>
                    <div className="therapist-card-badges" data-testid={`therapist-badges-${t.id}`}>
                      {Number(t.experience_years) >= 5 && (
                        <span className="therapist-card-badge">{t.experience_years}+ yrs</span>
                      )}
                      {t.approved !== false && (
                        <span className="therapist-card-badge therapist-card-badge-verified"><CheckCircle size={10} weight="fill" /> Verified</span>
                      )}
                    </div>
                  </div>
                </div>
                <p className="mt-4 text-sm break-words" style={{ color: "var(--md-text-soft)", overflowWrap: "anywhere" }}>{(t.bio || "").slice(0, 110)}…</p>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {(t.specialisations || []).slice(0, 3).map((s) => (
                    <span key={s} className="therapist-card-pill">{s}</span>
                  ))}
                </div>
                {(t.languages?.length || t.next_available_slot) && (
                  <div className="therapist-card-meta-row" data-testid={`therapist-meta-${t.id}`}>
                    {t.languages?.length > 0 && (
                      <span className="therapist-card-meta-chip" data-testid={`therapist-langs-${t.id}`}>
                        <ChatCircle size={12} weight="duotone" /> {t.languages.slice(0, 3).join(", ")}
                      </span>
                    )}
                    {t.next_available_slot && (
                      <span className="therapist-card-meta-chip therapist-card-meta-chip-accent" data-testid={`therapist-next-${t.id}`}>
                        <Clock size={12} weight="duotone" /> Next available: {t.next_available_slot}
                      </span>
                    )}
                  </div>
                )}
                {t.intro_audio_url && (
                  <IntroAudioPlayer src={audioSrc(t.intro_audio_url)} testid={t.id} />
                )}
                <IntroAudioSummary summary={t.intro_audio_summary} testid={t.id} />
                <div className="flex items-center justify-end mt-5 pt-4 border-t" style={{ borderColor: "var(--md-border)" }}>
                  <div className="text-sm font-medium" style={{ color: "var(--md-primary)" }}>
                    From {format(t.session_fee || 0)} <ArrowRight size={12} weight="bold" className="inline" />
                  </div>
                </div>
              </Link>
            ))}
            </div>
          );
        })()}
      </section>
      <DirectoryHelpBand kind="therapist" />
    </Page>
  );
}
